import React, { useState, useEffect, useRef } from "react";
import PreviaRemision from "./PreviaRemision";
import { useLocation } from "react-router-dom";
import ResumenRemision from "./ResumenRemision";
import BandejaRemisiones from "./BandejaRemisiones";
import Nav from "./Nav";
import RemisionForm from "./RemisionForm";
import { deleteDoc, doc, onSnapshot, updateDoc } from "firebase/firestore";
import Swal from "sweetalert2";
import Modal from "react-modal";
import moment from "moment";
import Spinner from "./Spinner";
import Paginador from "./Paginador";
import { useNavContext } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import { getSubCollection } from "../utils/getSubCollection";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import Breadcrumb from "./Breadcrumb";
import useFileUpload from "../hooks/useFileUpload";

moment.locale("es");

const styleForm = {
  content: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    paddingLeft: 0,
    padding: 0,
    fontFamily: "Inter, sans-serif",
    "::WebkitScrollbar": {
      width: "0px",
      background: "transparent",
    },
    /* Ocultar scrollbar en IE y Edge */
    msOverflowStyle: "none",
    /* Ocultar scrollbar en Firefox */
    scrollbarWidth: "none",
  },
};

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    border: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    maxWidth: "800px",
    width: "100%",
    height: "550px",
    maxHeight: "90vh",
    overflow: "auto",
    fontFamily: "Inter, sans-serif", // Aplica la fuente
  },
  label: {
    display: "block",
    marginBottom: "10px",
  },
  input: {
    width: "100%",
    padding: "8px",
    marginBottom: "20px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  button: {
    width: "50%", // Centra el botón de agregar
    padding: "10px",
    borderRadius: "5px",
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    display: "flex",
    justifyContent: "center",
    margin: "0 auto",
    fontWeight: "700",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
};

function TablaRemisiones({
  remisiones,
  clientes,
  setRemisiones,
  guardarRemision,
  modoEdicion,
  fechaRemision,
  remision,
  numeroRemision,
}) {
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;
  const { formatDate } = useFunctionsPrevia();
  const [cloneMode, setCloneMode] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [remisionSeleccionada, setRemisionSeleccionada] = useState(null);
  const [loadingRemisiones, setLoadingRemisiones] = useState(true);
  const [selectedRemisiones, setSelectedRemisiones] = useState([]);
  const [ordenamiento, setOrdenamiento] = useState({
    campo: "fechaRemision",
    ascendente: true,
  });
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [selectedRemisionId, setSelectedRemisionId] = useState(null);
  const [resumenVisible, setResumenVisible] = useState(false);
  const [showBandeja, setShowBandeja] = useState(false);
  const location = useLocation();
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [showNuevoButton, setShowNuevoButton] = useState(true);
  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();
  const setRemisionesRef = useRef(setRemisiones);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25); // Valor inicial
  const [estadosCeldas, setEstadosCeldas] = useState({});
  const [formDirty, setFormDirty] = useState(false);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  const handleFileUpload = useFileUpload({
    ownerUid, // tu uid del usuario/equipo
    setData: setRemisiones, // setter para actualizar el estado local
    docType: "remisiones", // tipo de documento según docConfigs
  });

  useEffect(() => {
    if (location.state && location.state.openModal) {
      setMostrarFormulario(true);
    }
  }, [location]);

  useEffect(() => {
    setLoadingRemisiones(true);

    // Obtenemos la referencia a la subcolección bajo el usuario
    const remisionesCol = getSubCollection(ownerUid, "remisiones");

    const unsubscribe = onSnapshot(
      remisionesCol,
      (snapshot) => {
        const updatedRemisiones = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Extraemos los estados de cada remisión
        const estados = updatedRemisiones.reduce((acc, r) => {
          acc[r.id] = r.estado || "Pendiente";
          return acc;
        }, {});
        setEstadosCeldas(estados);

        // Actualizamos la lista en el ref para poder propagarla hacia arriba
        setRemisionesRef.current(updatedRemisiones);

        setLoadingRemisiones(false);
      },
      (error) => {
        console.error("Error suscribiéndose a remisiones:", error);
        setLoadingRemisiones(false);
      }
    );

    return () => unsubscribe();
  }, [ownerUid]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptionsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const ordenarRemisiones = (remisiones, { campo, ascendente }) => {
    if (!remisiones || remisiones.length === 0) {
      return [];
    }
    const sortedRemisiones = [...remisiones];
    switch (campo) {
      case "fechaRemision":
        sortedRemisiones.sort(
          (a, b) =>
            (ascendente ? 1 : -1) *
            (new Date(a.fechaRemision) - new Date(b.fechaRemision))
        );
        break;
      case "numeroRemision":
        sortedRemisiones.sort(
          (a, b) =>
            (ascendente ? 1 : -1) * (a.numeroRemision - b.numeroRemision)
        );
        break;
      case "asunto":
        sortedRemisiones.sort(
          (a, b) => (ascendente ? 1 : -1) * a.asunto.localeCompare(b.asunto)
        );
        break;
      case "nombreCliente":
        sortedRemisiones.sort(
          (a, b) =>
            (ascendente ? 1 : -1) *
            a.nombreCliente.localeCompare(b.nombreCliente)
        );
        break;
      case "total":
        sortedRemisiones.sort(
          (a, b) => (ascendente ? 1 : -1) * (a.total - b.total)
        );
        break;
      case "referencia":
        sortedRemisiones.sort(
          (a, b) =>
            (ascendente ? 1 : -1) *
            (new Date(a.referencia) - new Date(b.referencia))
        );
        break;
      default:
        break;
    }
    return sortedRemisiones;
  };

  const filteredRemisiones = React.useMemo(() => {
    if (!remisiones || remisiones.length === 0) return []; // Devuelve un arreglo vacío si no hay datos
    const searchLower = searchTerm.toLowerCase();

    return remisiones.filter((remision) => {
      const searchableFields = [
        remision.fechaRemision,
        remision.numeroRemision?.toString().toLowerCase() || "",
        remision.asunto?.toLowerCase(),
        remision.nombreCliente?.toLowerCase(),
        remision.total?.toString().toLowerCase() || "",
        remision.referencia?.toString(),
        remision.estado?.toLowerCase() || "",
      ];

      return searchableFields.some(
        (field) => field && field.includes(searchLower)
      );
    });
  }, [remisiones, searchTerm]);

  // Función para alternar entre los estados de cada celda seleccionada y guardarlo en Firestore
  const toggleEstado = async (remisionId) => {
    // 1) Calculamos el siguiente estado en el ciclo
    const newEstado =
      estadosCeldas[remisionId] === "Pendiente"
        ? "En Proceso"
        : estadosCeldas[remisionId] === "En Proceso"
        ? "Facturado"
        : estadosCeldas[remisionId] === "Facturado"
        ? "Pagada"
        : "Pendiente";

    // 2) Actualizamos el estado localmente para feedback inmediato
    setEstadosCeldas((prev) => ({
      ...prev,
      [remisionId]: newEstado,
    }));

    // 3) Guardamos el cambio en Firestore en /usuarios/{ownerUid}/remisiones/{remisionId}
    const remisionesCol = getSubCollection(ownerUid, "remisiones");
    const remisionDocRef = doc(remisionesCol, remisionId);
    await updateDoc(remisionDocRef, { estado: newEstado });
  };

  const toggleOptionsMenu = () => {
    setShowOptionsMenu((prevShowOptionsMenu) => !prevShowOptionsMenu);
  };

  const handleImportClick = () => {
    fileInputRef.current.click(); // Simula el clic en el input de archivo
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const sortedRemisiones = ordenarRemisiones(filteredRemisiones, ordenamiento);

  const totalRemisiones = filteredRemisiones.length;

  const handleFormDirtyChange = (dirty) => {
    setFormDirty(dirty);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
  };

  const handleOpenClonarForm = (remisionClonada, isClone = false) => {
    setRemisionSeleccionada(remisionClonada);
    setCloneMode(isClone);
    setMostrarFormulario(true);
  };

  const openFormulario = () => {
    setMostrarFormulario(true);
    setShowNuevoButton(false); // Oculta el botón + Nuevo al abrir el formulario
  };

  const closeModal = () => {
    if (formDirty) {
      Swal.fire({
        title: "¿Seguro que quieres salir?",
        text: "Se perderán los cambios realizados",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          setMostrarFormulario(false);
          setShowNuevoButton(true); // Vuelve a mostrar el botón + Nuevo al cerrar el formulario
          setFormDirty(false); // Restablece formDirty
        }
      });
    } else {
      setMostrarFormulario(false);
      setRemisionSeleccionada(null);
      setShowNuevoButton(true);
      setCloneMode(false);
      setFormDirty(false); // Restablece formDirty
    }
  };

  const handleDeleteSelected = async () => {
    try {
      const confirmed = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción eliminará las remisiones seleccionadas.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        scrollbarPadding: false,
      });

      if (confirmed.isConfirmed) {
        const remisionesEliminadas = [];
        const remisionesCol = getSubCollection(ownerUid, "remisiones");

        for (const remisionId of selectedRemisiones) {
          const remisionDocRef = doc(remisionesCol, remisionId);
          await deleteDoc(remisionDocRef);
          remisionesEliminadas.push(remisionId);
        }

        // Actualizamos el estado local eliminando las borradas
        setRemisiones((prev) =>
          prev.filter((r) => !remisionesEliminadas.includes(r.id))
        );
        setSelectedRemisiones([]);

        await Swal.fire({
          title: "¡Remisión eliminada!",
          text: "Las remisiones seleccionadas han sido eliminadas correctamente.",
          icon: "success",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "Ok",
          scrollbarPadding: false,
        });
      }
    } catch (error) {
      console.error("Error al eliminar remisiones:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron eliminar las remisiones.",
      });
    }
  };

  const cerrarModalPrevia = () => {
    setModalIsOpen(false);
    setRemisionSeleccionada(null);
  };

  const handleRowClick = (remisionId) => {
    setSelectedRemisionId(remisionId);
    setResumenVisible(true);
    setShowBandeja(true);
  };

  const handleSelectRemision = (remisionId) => {
    setSelectedRemisiones((prevSelected) => {
      if (prevSelected.includes(remisionId)) {
        return prevSelected.filter((id) => id !== remisionId);
      } else {
        return [...prevSelected, remisionId];
      }
    });
  };

  const handleOrdenamientoChange = (campo) => {
    setOrdenamiento((prevOrdenamiento) => ({
      campo,
      ascendente:
        campo === prevOrdenamiento.campo ? !prevOrdenamiento.ascendente : true,
    }));
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Si se marca el checkbox de "seleccionar todos", selecciona todas las remisiones
      const allRemisionesIds = filteredRemisiones.map(
        (remision) => remision.id
      );
      setSelectedRemisiones(allRemisionesIds);
    } else {
      // Si se desmarca, vacía la lista de seleccionados
      setSelectedRemisiones([]);
    }
  };

  return (
    <>
      <div className="no-sticky">
        {!showBandeja && !resumenVisible && !mostrarFormulario && (
          <>
            <Nav
              handleSearch={setSearchTerm}
              proximasAVencer={proximasAVencer}
              proximosEventos={proximosEventos}
            />

            <div className="task-filters" style={{ flexWrap: "wrap" }}>
              <div
                style={{
                  flexBasis: "100%", // ocupa toda la fila
                  padding: "0 4px", // mismo gutter que usas
                  margin: "0 0 5px", // respirito arriba/abajo
                }}
              >
                <Breadcrumb />
              </div>
              <div className="header-actions-proyectos">
                <button className="action-button-proyectos">
                  <i className="bi bi-funnel-fill"></i> Todos
                  <i
                    className="bi bi-chevron-down"
                    style={{ fontSize: "12px" }}
                  ></i>
                </button>
                <div className="buscador-pedidos">
                  <i className="bi bi-search"></i>
                  <input
                    type="text"
                    className="buscador-input"
                    placeholder="Buscar remisiones..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="task-filters-right">
                <div
                  className="view-buttons-container"
                  style={{ position: "relative" }}
                >
                  {/* Botón de Eliminar a la izquierda del botón de los tres puntos */}
                  <div className="delete-button-container">
                    <button
                      type="button"
                      className="delete-button"
                      style={{
                        visibility:
                          selectedRemisiones.length > 0 ? "visible" : "hidden",
                        opacity: selectedRemisiones.length > 0 ? 1 : 0,
                        transform:
                          selectedRemisiones.length > 0
                            ? "scale(1)"
                            : "scale(0.9)",
                        transition:
                          "visibility 0s linear 0.2s, opacity 0.2s ease-in-out, transform 0.2s ease-in-out",
                      }}
                      onClick={handleDeleteSelected}
                    >
                      Eliminar
                    </button>
                  </div>
                  <button className="view-button" onClick={toggleOptionsMenu}>
                    <i className="bi bi-three-dots-vertical"></i>
                  </button>
                  {showOptionsMenu && (
                    <div className="dropdown-options" ref={dropdownRef}>
                      <p onClick={handleImportClick}>
                        <i
                          className="bi bi-box-arrow-in-down"
                          style={{ marginRight: 10 }}
                        ></i>
                        Importar
                      </p>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept=".xlsx, .xls"
                        onChange={handleFileUpload}
                      />
                      <p>
                        <i
                          className="bi bi-box-arrow-up"
                          style={{ marginRight: 10 }}
                        ></i>
                        Exportar
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  {showNuevoButton && (
                    <button
                      className="new-task-button"
                      onClick={openFormulario}
                    >
                      + Crear Nuevo
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        <div className="cotizaciones-table">
          {showBandeja && (
            <BandejaRemisiones
              remisiones={remisiones}
              onRowClick={handleRowClick}
              clientes={clientes}
              guardarRemision={guardarRemision}
              remision={remision}
            />
          )}
          {!showBandeja && (
            <div className="tabla-cotizaciones-wrapper">
              <div className="tabla-cotizaciones-content">
                {loadingRemisiones ? (
                  <Spinner />
                ) : (
                  <>
                    {remisiones.length === 0 ? (
                      // 1) Aún no hay remisiones
                      <div className="no-resultados">
                        <div
                          className="mensaje-sin-resultados"
                          style={{ marginTop: "30px" }}
                        >
                          <p style={{ fontSize: "14px" }}>
                            Aún no hay ninguna remisión.
                          </p>
                        </div>
                        <div className="imagen-sin-resultados">
                          <img
                            src="/img/no-data.svg"
                            alt="Sin remisiones"
                            width={350}
                            height={350}
                          />
                        </div>
                      </div>
                    ) : filteredRemisiones.length > 0 ? (
                      // 2) Hay remisiones tras el filtro
                      <table>
                        {!mostrarFormulario && (
                          <thead>
                            <tr>
                              <th>
                                <input
                                  type="checkbox"
                                  onChange={handleSelectAll}
                                  className="checkbox-select-all check"
                                  checked={
                                    selectedRemisiones.length ===
                                    filteredRemisiones.length
                                  }
                                />
                              </th>
                              <th
                                onClick={() =>
                                  handleOrdenamientoChange("fechaRemision")
                                }
                              >
                                Fecha
                              </th>
                              <th
                                onClick={() =>
                                  handleOrdenamientoChange("numeroRemision")
                                }
                              >
                                No.
                              </th>
                              <th
                                onClick={() =>
                                  handleOrdenamientoChange("referencia")
                                }
                              >
                                Referencia
                              </th>
                              <th
                                onClick={() =>
                                  handleOrdenamientoChange("nombreCliente")
                                }
                                style={{ width: "20%" }}
                              >
                                Cliente
                              </th>
                              <th
                                onClick={() =>
                                  handleOrdenamientoChange("estado")
                                }
                              >
                                Estado
                              </th>
                              <th
                                onClick={() =>
                                  handleOrdenamientoChange("total")
                                }
                              >
                                Importe
                              </th>
                            </tr>
                          </thead>
                        )}
                        <tbody>
                          {sortedRemisiones.map((r) => (
                            <tr key={r.id}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedRemisiones.includes(r.id)}
                                  onChange={() => handleSelectRemision(r.id)}
                                  className="checkbox"
                                />
                              </td>
                              <td onClick={() => handleRowClick(r.id)}>
                                <div className="estilos-fechas">
                                  {formatDate(r.fechaRemision)}
                                </div>
                              </td>
                              <td
                                style={{ color: "#007bff", fontWeight: "500" }}
                                onClick={() => handleRowClick(r.id)}
                              >
                                {r.numeroRemision
                                  ? r.numeroRemision.toString().padStart(4, "0")
                                  : "N/A"}
                              </td>
                              <td onClick={() => handleRowClick(r.id)}>
                                {r.referencia}
                              </td>
                              <td onClick={() => handleRowClick(r.id)}>
                                {r.nombreCliente || "Cliente desconocido"}
                              </td>
                              <td>
                                <div
                                  className={`status ${
                                    estadosCeldas[r.id] === "Pendiente"
                                      ? "pendiente"
                                      : estadosCeldas[r.id] === "En Proceso"
                                      ? "en-proceso"
                                      : estadosCeldas[r.id] === "Facturado"
                                      ? "facturado"
                                      : estadosCeldas[r.id] === "Pagada"
                                      ? "pagada"
                                      : "pendiente"
                                  }`}
                                  onClick={() => toggleEstado(r.id)}
                                >
                                  {estadosCeldas[r.id] || "Pendiente"}
                                </div>
                              </td>
                              <td
                                className="importe"
                                onClick={() => handleRowClick(r.id)}
                              >
                                {r.divisa}{" "}
                                {parseFloat(r.total)
                                  .toFixed(2)
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      // 3) No hay coincidencias tras búsqueda
                      <div className="no-resultados">
                        <div
                          className="mensaje-sin-resultados"
                          style={{ marginTop: "30px" }}
                        >
                          <p style={{ fontSize: "14px" }}>
                            No existe ninguna remisión que coincida con esta
                            búsqueda.
                          </p>
                        </div>
                        <div className="imagen-sin-resultados">
                          <img
                            src="/img/noSearch.svg"
                            alt="Sin resultados"
                            className="sin-resultados-imagen"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}

                <Modal
                  isOpen={mostrarFormulario}
                  onRequestClose={closeModal}
                  contentLabel="Nueva Remisión"
                  style={styleForm}
                >
                  <button onClick={closeModal} className="cerrar-button">
                    <i className="bi bi-x-lg" />
                  </button>
                  <RemisionForm
                    clientes={clientes}
                    guardarRemision={guardarRemision}
                    modoEdicion={modoEdicion}
                    cloneMode={cloneMode}
                    remision={remisionSeleccionada}
                    onFormDirtyChange={handleFormDirtyChange}
                    closeModal={closeModal}
                  />
                </Modal>
              </div>

              <div className="tabla-cotizaciones-footer">
                <div className="paginador-container">
                  <Paginador
                    totalItems={totalRemisiones}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                  />
                </div>
              </div>
            </div>
          )}

          <Modal
            isOpen={modalIsOpen}
            onRequestClose={cerrarModalPrevia}
            contentLabel="Vista Previa"
            style={customStyles}
          >
            {remisionSeleccionada && (
              <PreviaRemision
                remision={remisionSeleccionada}
                numeroRemision={remisionSeleccionada.numeroRemision}
                clientes={clientes}
                cerrarPrevia={cerrarModalPrevia}
              />
            )}
          </Modal>

          <div
            className={`resumen-container ${
              selectedRemisionId ? "active" : ""
            }`}
          >
            <ResumenRemision
              remision={
                remisiones &&
                remisiones.find(
                  (remision) => remision.id === selectedRemisionId
                )
              }
              isOpen={resumenVisible}
              onClose={() => {
                setResumenVisible(false);
                setSelectedRemisionId(null);
                setShowBandeja(false);
              }}
              numeroRemision={
                remisionSeleccionada
                  ? remisionSeleccionada.numeroRemision
                  : null
              }
              clientes={clientes}
              mostrarBotonNuevo={false}
              onClonar={handleOpenClonarForm}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default TablaRemisiones;
